/**
 * Workspace Scope Context Definition
 * 
 * Contains ONLY the context creation and type definitions.
 * NO hooks, NO providers - just the context itself.
 */

import { createContext } from 'react';
import { WorkspaceScope } from '@/lib/workspaceScope';

export interface WorkspaceInfo {
  workspace_id: string;
  workspace_name: string;
  role: 'owner' | 'admin' | 'member';
  created_at: string;
  /** Billing plan: source of truth from workspaces.plan (set by Stripe webhook). */
  plan?: 'base' | 'pro';
}

export type WorkspaceMode = 'single' | 'all';

export interface WorkspaceScopeContextValue {
  // State
  mode: WorkspaceMode;
  activeWorkspaceId: string | null;
  workspaceIds: string[]; // All accessible workspace IDs
  workspaces: WorkspaceInfo[];
  isSwitchingWorkspace: boolean; // True during atomic workspace switch
  
  // Actions
  setMode: (mode: WorkspaceMode, workspaceId?: string | null) => void;
  setActiveWorkspace: (workspaceId: string) => void;
  setAllMode: () => void;
  
  // Computed
  scope: WorkspaceScope;
  effectiveWorkspaceIds: string[]; // IDs to use in queries (single => [activeWorkspaceId], all => workspaceIds)
  isLoading: boolean;
  error: { message: string; code?: string } | null;
  
  // Helpers
  refetchWorkspaces: () => void;
}

// Export context for direct useContext access
export const WorkspaceScopeContext = createContext<WorkspaceScopeContextValue | undefined>(undefined);

